import {
    Alert,
    Box,
    Button,
    Chip,
    IconButton,
    MenuItem,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useMemo, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import TitleSetter from '../../components/TitleSetter';
import { TabPanel } from '../../components/TabPanel';
import Loading from '../../components/Loading';
import BackdropComponent from '../../components/Backdrop/BackdropComponent';
import { getDate } from '../../utils/common';
import {
    useCancelarSiniestroMutation,
    useFinalizarSiniestroMutation,
    useGetMotivosCancelacionSiniestroQuery,
    useGetSiniestroByIdQuery,
    useGetSiniestroVisitasQuery
} from '../../services/roamApi';
import useUserAccess from '../../hooks/useUserAccess';
import { getAccessValues } from '../../components/layout/RoutesManager/access';
import { useRoles } from '../../hooks/useRoles';
import useRoutePrefix from '../../hooks/useRoutePrefix';
import SemaforoSRT from './components/SemaforoSRT';
import SiniestroInformationTab from './components/SiniestroInformationTab';
import SiniestroVisitsTab from './components/SiniestroVisitsTab';
import SiniestroSrtTab from './components/SiniestroSrtTab';
import { VisitaEstado } from '../Visitas/consts/visitasStatus';
import { AlertDialog } from '../../components/Alert/AlertDialog';
import { canEditOccurrenceLocation } from './canEditOccurrenceLocation';
import { tieneLugarOcurrenciaCompleto } from './utils/lugarOcurrencia';

const INFORMATION_TAB_INDEX = 0;
const VISITS_TAB_INDEX = 1;
const SRT_TAB_INDEX = 2;
const SHOW_SRT_TAB = true;
const SINIESTRO_FINALIZABLE_VISIT_STATES = [
    VisitaEstado.PRESENTADO,
    VisitaEstado.CANCELADA,
    VisitaEstado.FALLIDA
];

const getEstadoConfig = (idEstado: number | undefined, t: (key: string) => string) => {
    if (idEstado === 1) {
        return {
            label: t('detail.status.pending'),
            color: '#0F5B99',
            backgroundColor: '#EAF4FF',
            borderColor: '#C9E0FF'
        };
    }

    if (idEstado === 2) {
        return {
            label: t('detail.status.open'),
            color: '#0F5B99',
            backgroundColor: '#EAF4FF',
            borderColor: '#C9E0FF'
        };
    }

    if (idEstado === 3) {
        return {
            label: t('detail.status.finalized'),
            color: '#1C6B2A',
            backgroundColor: '#EAF7ED',
            borderColor: '#CBE8D2'
        };
    }

    if (idEstado === 4) {
        return {
            label: t('detail.status.cancelled'),
            color: '#B42318',
            backgroundColor: '#FEECEC',
            borderColor: '#F5CACA'
        };
    }

    return {
        label: t('detail.placeholders.empty'),
        color: '#4B5563',
        backgroundColor: '#F3F4F6',
        borderColor: '#D1D5DB'
    };
};

const getTipoLabel = (idTipoSiniestro: number | undefined, t: (key: string) => string) => {
    if (idTipoSiniestro === 1) return t('detail.types.workAccident');
    if (idTipoSiniestro === 3) return t('detail.types.commuteAccident');
    if (idTipoSiniestro === 6) return t('detail.types.occupationalDisease');
    return t('detail.placeholders.empty');
};

const getSimpOrganizationLabel = (datosSimp?: any) => {
    const organismo = datosSimp?.organismo || datosSimp?.establecimiento?.organismo;

    if (typeof organismo === 'string' && organismo.trim()) return organismo;

    return organismo?.nombre || organismo?.organismoCentral?.nombre || '';
};

const getSimpContractLabel = (datosSimp?: any) => {
    const contrato = datosSimp?.contrato;
    const organismo = datosSimp?.organismo || datosSimp?.establecimiento?.organismo;
    const organismoCentral = organismo?.organismoCentral;

    if (typeof contrato === 'string' && contrato.trim()) return contrato;
    if (typeof contrato === 'number') return String(contrato);

    return (
        contrato?.nombre ||
        organismoCentral?.nombre ||
        contrato?.nroContrato ||
        contrato?.contrato ||
        organismoCentral?.contrato ||
        organismoCentral?.nroContrato ||
        organismo?.nroContrato ||
        ''
    );
};

const getSimpEstablishmentLabel = (datosSimp?: any) => {
    const establecimiento = datosSimp?.establecimiento;

    if (typeof establecimiento === 'string' && establecimiento.trim()) return establecimiento;

    return establecimiento?.nombre || '';
};

const getSimpTextLabel = (value: any) => {
    if (typeof value === 'string' && value.trim()) return value;
    if (typeof value === 'number') return String(value);
    if (value?.nombre) return value.nombre;

    return '';
};

const hasSimpPresentation = (datosSimp: any, presentationTypeId: number) => {
    const presentations = datosSimp?.establecimiento?.presentaciones || [];

    return presentations.some((presentation: any) => {
        const typeId = Number(presentation?.tipo?.id || presentation?.idTipoPresentacion);

        return typeId === presentationTypeId;
    });
};

const getSimpPresentationFlag = (datosSimp: any, presentationTypeId: number) => {
    if (!datosSimp?.establecimiento) return '-';

    return hasSimpPresentation(datosSimp, presentationTypeId) ? 'SI' : 'NO';
};

interface SummaryFieldProps {
    label: string;
    value?: string | null;
    isLoading: boolean;
}

const SummaryField = ({ label, value, isLoading }: SummaryFieldProps) => (
    <Box>
        <Typography
            variant="caption"
            sx={{
                display: 'block',
                mb: 0.75,
                color: '#98A2B3',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '.04em'
            }}
        >
            {label}
        </Typography>
        {isLoading ? (
            <Loading size="extrasmall" position="flex-start" />
        ) : (
            <Typography variant="body2" sx={{ color: '#344054', fontWeight: 600 }}>
                {value || '-'}
            </Typography>
        )}
    </Box>
);

function SiniestroDetail() {
    const { t } = useTranslation('siniestrosRoam');
    const access = useUserAccess();
    const FEATURES = getAccessValues(access);
    const { isPreventor, isSupervisorSIMP } = useRoles();
    const routePrefix = useRoutePrefix();
    const navigate = useNavigate();
    const location = useLocation();
    const [currentTab, setCurrentTab] = useState(INFORMATION_TAB_INDEX);
    const siniestroId = location.state?.siniestroId as number | undefined;
    // Desde Gestión de Archivos SRT se vuelve a esa pantalla y no al listado de ROAM (SMP-561)
    const volverA = location.state?.volverA as string | undefined;

    const {
        data: siniestro,
        isFetching,
        isError,
        refetch: refetchSiniestro
    } = useGetSiniestroByIdQuery(siniestroId as number, {
        skip: !siniestroId
    });
    const { data: visitas = [], isFetching: isFetchingVisitas } = useGetSiniestroVisitasQuery(
        siniestroId as number,
        {
            skip: !siniestroId
        }
    );
    const [finalizarSiniestro, { isLoading: isFinalizing }] = useFinalizarSiniestroMutation();
    const [cancelarSiniestro, { isLoading: isCancelling }] = useCancelarSiniestroMutation();
    // El supervisor solo consulta: no cancela ni finaliza, ni crea visitas (SMP-460)
    const { data: cancelReasonOptions = [] } = useGetMotivosCancelacionSiniestroQuery(undefined, {
        skip: isSupervisorSIMP
    });
    const isInitialLoading = isFetching && !siniestro;
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [cancelReason, setCancelReason] = useState<number | ''>('');
    const [cancelReasonError, setCancelReasonError] = useState(false);

    const tipo = getTipoLabel(siniestro?.idTipoSiniestro, t);
    const estado = getEstadoConfig(siniestro?.idEstado, t);
    const datosSimp = siniestro?.datosSimp || {};
    const simpPatientName = [datosSimp?.apellido, datosSimp?.nombre]
        .filter(Boolean)
        .join(' ')
        .trim();
    const simpOrganization = getSimpOrganizationLabel(datosSimp) || t('detail.placeholders.empty');
    const simpContract = getSimpContractLabel(datosSimp) || t('detail.placeholders.empty');
    const simpEstablishment =
        getSimpEstablishmentLabel(datosSimp) || t('detail.placeholders.empty');
    const simpHasRar = getSimpPresentationFlag(datosSimp, 4);
    const simpHasSvc = getSimpPresentationFlag(datosSimp, 5);
    const headerEstablishment = datosSimp?.establecimiento?.nombre;
    const headerSubtitle = [headerEstablishment, tipo]
        .filter((value) => value && value !== t('detail.placeholders.empty'))
        .join(' - ');
    const linkedEstablishmentId =
        Number(
            siniestro?.establecimiento?.id ||
                datosSimp?.establecimiento?.id ||
                datosSimp?.idEstablecimiento ||
                siniestro?.datosSimp?.idEstablecimiento ||
                siniestro?.lugarTrabajo?.idEstablecimiento ||
                siniestro?.lugarTrabajo?.id ||
                siniestro?.idEstablecimiento
        ) || null;

    const hasVisits = visitas.length > 0;
    const canCreateVisit = tieneLugarOcurrenciaCompleto(siniestro);
    const canEditLugarOcurrencia =
        !isSupervisorSIMP && canEditOccurrenceLocation(siniestro?.idEstado, visitas);
    const canCancelSiniestro =
        FEATURES.CANCELACION_SINIESTRO_ROAM.hasAccess &&
        Boolean(siniestroId) &&
        !isFetching &&
        siniestro?.idEstado === 1;
    const shouldShowFinalizeSiniestro =
        !isSupervisorSIMP &&
        Boolean(siniestro?.idEstado) &&
        siniestro?.idEstado !== 1 &&
        siniestro?.idEstado !== 4;
    const cancelDialogTitle = String(t('table.cancelDialog.title'));
    const cancelDialogMessage = String(
        t('table.cancelDialog.message', {
            id: siniestro?.nroSiniestro || siniestro?.id || '-'
        })
    );
    const occurrenceLocationState = {
        calle: siniestro?.lugarOcurrencia?.calle || '',
        altura: siniestro?.lugarOcurrencia?.altura || '',
        piso: siniestro?.lugarOcurrencia?.piso || '',
        depto: siniestro?.lugarOcurrencia?.depto || '',
        codigoPostal: siniestro?.lugarOcurrencia?.codigoPostal || '',
        localidad:
            siniestro?.lugarOcurrencia?.localidad?.nombre ||
            (siniestro?.establecimiento?.direccion?.localidad?.id ===
            siniestro?.lugarOcurrencia?.idLocalidad
                ? siniestro?.establecimiento?.direccion?.localidad?.nombre || ''
                : ''),
        provincia:
            siniestro?.lugarOcurrencia?.localidad?.provincia?.nombre ||
            (siniestro?.establecimiento?.direccion?.localidad?.id ===
            siniestro?.lugarOcurrencia?.idLocalidad
                ? siniestro?.establecimiento?.direccion?.localidad?.provincia?.nombre || ''
                : ''),
        idLocalidad:
            siniestro?.lugarOcurrencia?.localidad?.id ||
            siniestro?.lugarOcurrencia?.idLocalidad ||
            null
    };

    const handleFinalizeSiniestro = async () => {
        if (!siniestroId || isFinalizing) return;

        const hasVisitsInProcess = visitas.some(
            (visita: any) =>
                visita?.idEstado === VisitaEstado.PENDIENTE ||
                visita?.idEstado === VisitaEstado.REALIZADA
        );

        const canFinalizeSiniestro =
            hasVisits &&
            visitas.every(
                (visita: any) =>
                    visita?.idEstado !== undefined &&
                    SINIESTRO_FINALIZABLE_VISIT_STATES.includes(visita.idEstado)
            );

        if (hasVisitsInProcess || !canFinalizeSiniestro) {
            enqueueSnackbar(t('detail.alerts.visitsInProgress'), { variant: 'warning' });
            return;
        }

        try {
            await finalizarSiniestro(siniestroId).unwrap();
            enqueueSnackbar(t('detail.alerts.finalizeSuccess'), { variant: 'success' });
        } catch (error: any) {
            enqueueSnackbar(error?.data?.message || t('detail.alerts.finalizeError'), {
                variant: 'error'
            });
        }
    };

    const handleCreateVisit = () => {
        if (!canCreateVisit) {
            enqueueSnackbar(t('detail.occurrenceLocation.alerts.validVisitRequired'), {
                variant: 'warning'
            });
            return;
        }

        navigate(`/${routePrefix}/visitas/crear`, {
            state: {
                idSiniestro: siniestroId || null,
                nroSiniestro: siniestro?.nroSiniestro || siniestroId || null,
                nroRoam: siniestro?.nroRoam || null,
                idEstablecimiento: linkedEstablishmentId,
                patientName: simpPatientName || null,
                patientCuil: datosSimp?.cuil || null,
                selectedEstablishment: linkedEstablishmentId
                    ? {
                          id: linkedEstablishmentId,
                          nombre: simpEstablishment
                      }
                    : null,
                occurrenceLocation: occurrenceLocationState,
                idTipoSiniestro: siniestro?.idTipoSiniestro ?? null,
                fechaAltaRoam: siniestro?.fechaAltaRoam ?? null,
                patologiasTrazadoras: siniestro?.datosMedicos?.patologiasTrazadoras ?? null
            }
        });
    };

    const handleOpenCancelDialog = () => {
        setCancelReason('');
        setCancelReasonError(false);
        setCancelDialogOpen(true);
    };

    const handleCloseCancelDialog = () => {
        setCancelDialogOpen(false);
        setCancelReason('');
        setCancelReasonError(false);
    };

    const handleCancelSiniestro = async () => {
        if (!siniestroId || isCancelling) return;

        if (!cancelReason) {
            setCancelReasonError(true);
            return;
        }

        try {
            await cancelarSiniestro({
                id: siniestroId,
                body: { idMotivoCancelacion: cancelReason }
            }).unwrap();
            enqueueSnackbar(t('table.alerts.cancelSuccess'), { variant: 'success' });
            handleCloseCancelDialog();
            refetchSiniestro();
        } catch (error: any) {
            enqueueSnackbar(error?.data?.message || t('table.alerts.cancelError'), {
                variant: 'error'
            });
        }
    };

    const title = useMemo(
        () =>
            t('detail.title', {
                id:
                    siniestro?.nroRoam ||
                    siniestro?.nroSiniestro ||
                    siniestro?.id ||
                    siniestroId ||
                    '-'
            }),
        [siniestro, siniestroId, t]
    );

    return (
        <Box sx={{ mt: 2 }}>
            <TitleSetter title={t('detail.pageTitle')} />

            {!siniestroId && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {t('detail.warnings.missingId')}
                </Alert>
            )}

            {isError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {t('detail.warnings.loadError')}
                </Alert>
            )}

            <Box sx={{ p: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', md: 'center' },
                        gap: 2
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 1.5,
                            alignItems: 'flex-start'
                        }}
                    >
                        <IconButton
                            size="small"
                            onClick={() => navigate(volverA ?? `/${routePrefix}/siniestros`)}
                            sx={{ mt: 0.25 }}
                        >
                            <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <Box>
                            {isInitialLoading ? (
                                <Box sx={{ minWidth: 260 }}>
                                    <Loading size="small" position="flex-start" />
                                </Box>
                            ) : (
                                <>
                                    <Typography
                                        variant="h3"
                                        sx={{ fontWeight: 700, color: '#101828', lineHeight: 1.2 }}
                                    >
                                        {title}
                                    </Typography>
                                    <Box
                                        sx={{
                                            mt: 0.75,
                                            display: 'flex',
                                            flexDirection: 'row',
                                            gap: 1,
                                            alignItems: 'center',
                                            flexWrap: 'wrap'
                                        }}
                                    >
                                        {headerSubtitle && (
                                            <Typography variant="body2" sx={{ color: '#667085' }}>
                                                {headerSubtitle}
                                            </Typography>
                                        )}
                                        <Chip
                                            label={estado.label}
                                            size="small"
                                            sx={{
                                                height: 22,
                                                borderRadius: 1,
                                                fontSize: 11,
                                                fontWeight: 700,
                                                color: estado.color,
                                                backgroundColor: estado.backgroundColor,
                                                border: `1px solid ${estado.borderColor}`
                                            }}
                                        />
                                        <SemaforoSRT
                                            fechaVencimientoSRT={siniestro?.fechaVencimientoSRT}
                                            fechaPresentacionVisita={siniestro?.fechaPresentacionVisita}
                                            esPreventor={isPreventor ?? false}
                                        />
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {siniestro?.idEstado === 1 && !isSupervisorSIMP && (
                            <Button
                                variant="contained"
                                color="error"
                                disabled={!canCancelSiniestro || isCancelling}
                                onClick={handleOpenCancelDialog}
                                size="small"
                                sx={{
                                    minWidth: 152,
                                    height: 32,
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    boxShadow: 'none'
                                }}
                            >
                                {t('table.actions.cancel')}
                            </Button>
                        )}
                        {shouldShowFinalizeSiniestro && (
                            <Button
                                variant="contained"
                                disabled={
                                    !FEATURES.FINALIZACION_SINIESTRO_ROAM.hasAccess ||
                                    !siniestroId ||
                                    isFetching ||
                                    isFetchingVisitas ||
                                    isFinalizing ||
                                    siniestro?.idEstado === 3 ||
                                    siniestro?.idEstado === 4 ||
                                    !hasVisits
                                }
                                onClick={handleFinalizeSiniestro}
                                size="small"
                                sx={{
                                    minWidth: 152,
                                    height: 32,
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    boxShadow: 'none'
                                }}
                            >
                                {t('detail.actions.finalize')}
                            </Button>
                        )}
                    </Box>
                </Box>

                {siniestro?.idEstado === 4 && (
                    <Box
                        sx={{
                            mt: 3,
                            p: 2,
                            border: '1px solid #F5CACA',
                            borderRadius: 1.5,
                            backgroundColor: '#FEECEC',
                            color: '#B42318'
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#B42318' }}>
                            Motivo de cancelación
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.75, color: '#344054' }}>
                            {siniestro?.motivoCancelacion || t('detail.placeholders.empty')}
                        </Typography>
                    </Box>
                )}

                <Box
                    sx={{
                        mt: 3,
                        p: 2,
                        border: '1px solid #EAECF0',
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        backgroundColor: '#FCFCFD'
                    }}
                >
                    <Box
                        sx={{
                            px: 2,
                            py: 1.25,
                            borderBottom: '1px solid #EAECF0',
                            backgroundColor: '#FCFCFD'
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#344054' }}>
                            {t('detail.summary.simp')}
                        </Typography>
                    </Box>
                    <Box sx={{ p: 2 }}>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'repeat(2, 1fr)',
                                    lg: 'repeat(3, 1fr)'
                                },
                                gap: 3
                            }}
                        >
                            <SummaryField
                                label={t('detail.simp.name')}
                                value={datosSimp?.nombre}
                                isLoading={isInitialLoading}
                            />
                            <SummaryField
                                label={t('detail.simp.cuil')}
                                value={datosSimp?.cuil}
                                isLoading={isInitialLoading}
                            />
                            <SummaryField
                                label={t('detail.simp.entryDate')}
                                value={
                                    datosSimp?.fechaIngreso
                                        ? getDate(datosSimp.fechaIngreso)
                                        : t('detail.placeholders.empty')
                                }
                                isLoading={isInitialLoading}
                            />
                            <SummaryField
                                label={t('detail.simp.organization')}
                                value={simpOrganization}
                                isLoading={isInitialLoading}
                            />
                            <SummaryField
                                label={t('detail.simp.contract')}
                                value={simpContract}
                                isLoading={isInitialLoading}
                            />
                            <SummaryField
                                label={t('detail.simp.establishment')}
                                value={simpEstablishment}
                                isLoading={isInitialLoading}
                            />
                            <SummaryField
                                label={t('detail.simp.sector')}
                                value={
                                    getSimpTextLabel(datosSimp?.sector) ||
                                    t('detail.placeholders.empty')
                                }
                                isLoading={isInitialLoading}
                            />
                            <SummaryField
                                label={t('detail.simp.position')}
                                value={
                                    getSimpTextLabel(datosSimp?.puesto) ||
                                    t('detail.placeholders.empty')
                                }
                                isLoading={isInitialLoading}
                            />
                            <SummaryField
                                label={t('detail.simp.rar')}
                                value={simpHasRar}
                                isLoading={isInitialLoading}
                            />
                            <SummaryField
                                label="SVCC"
                                value={simpHasSvc}
                                isLoading={isInitialLoading}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ p: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'stretch', md: 'center' },
                        px: 2,
                        pt: 1.5,
                        borderBottom: '1px solid #EAECF0',
                        gap: 1.5
                    }}
                >
                    <Tabs
                        value={currentTab}
                        onChange={(_, value) => setCurrentTab(value)}
                        variant="scrollable"
                        allowScrollButtonsMobile
                    >
                        <Tab
                            label={t('detail.tabs.information')}
                            sx={{
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                px: 4,
                                py: 2,
                                textTransform: 'none'
                            }}
                        />
                        <Tab
                            label={t('detail.tabs.visits')}
                            sx={{
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                px: 4,
                                py: 2,
                                textTransform: 'none'
                            }}
                        />
                        {SHOW_SRT_TAB && (
                            <Tab
                                label={t('detail.tabs.srt')}
                                sx={{
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    px: 4,
                                    py: 2,
                                    textTransform: 'none'
                                }}
                            />
                        )}
                    </Tabs>

                    {currentTab === VISITS_TAB_INDEX &&
                        !isSupervisorSIMP &&
                        siniestro?.idEstado !== 3 &&
                        siniestro?.idEstado !== 4 && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                                onClick={handleCreateVisit}
                                disabled={
                                    !FEATURES.GESTION_VISITA_DESDE_SINIESTRO_ROAM.hasAccess ||
                                    !FEATURES.ALTA_DE_VISITAS.hasAccess
                                }
                                sx={{
                                    alignSelf: { xs: 'flex-start', md: 'center' },
                                    mb: { xs: 1.5, md: 1 },
                                    textTransform: 'none',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    boxShadow: 'none'
                                }}
                            >
                                {t('detail.actions.newVisit')}
                            </Button>
                        )}
                </Box>

                <Box>
                    <TabPanel value={currentTab} index={INFORMATION_TAB_INDEX}>
                        <SiniestroInformationTab
                            siniestro={siniestro}
                            siniestroId={siniestroId}
                            canEditOccurrenceLocation={canEditLugarOcurrencia}
                            isLoading={isInitialLoading}
                            onRefreshSiniestro={refetchSiniestro}
                        />
                    </TabPanel>
                    <TabPanel value={currentTab} index={VISITS_TAB_INDEX}>
                        <SiniestroVisitsTab siniestroId={siniestroId} />
                    </TabPanel>
                    {SHOW_SRT_TAB && (
                        <TabPanel value={currentTab} index={SRT_TAB_INDEX}>
                            <SiniestroSrtTab siniestro={siniestro} />
                        </TabPanel>
                    )}
                </Box>
            </Box>

            {/* {!isPreventor && <LogAuditoriaSection idSiniestroRoam={siniestroId} />} */}

            {cancelDialogOpen && (
                <AlertDialog
                    title={cancelDialogTitle}
                    message={cancelDialogMessage}
                    icon="cancel"
                    closeAction={handleCloseCancelDialog}
                    reactElement={
                        <TextField
                            select
                            fullWidth
                            label={t('table.cancelDialog.fieldLabel')}
                            value={cancelReason}
                            onChange={(event) => {
                                setCancelReason(Number(event.target.value));
                                setCancelReasonError(false);
                            }}
                            error={cancelReasonError}
                            helperText={
                                cancelReasonError ? t('table.cancelDialog.validation') : ' '
                            }
                            InputLabelProps={{ shrink: true }}
                            SelectProps={{ displayEmpty: true }}
                        >
                            <MenuItem value="" disabled>
                                {t('table.cancelDialog.placeholder')}
                            </MenuItem>
                            {cancelReasonOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.descripcion}
                                </MenuItem>
                            ))}
                        </TextField>
                    }
                    customButtons={
                        <Box display="flex" gap={2} width="100%">
                            <Button variant="outlined" fullWidth onClick={handleCloseCancelDialog}>
                                {t('table.cancelDialog.back')}
                            </Button>
                            <Button
                                variant="contained"
                                fullWidth
                                color="error"
                                onClick={handleCancelSiniestro}
                                disabled={isCancelling}
                            >
                                {t('table.cancelDialog.confirm')}
                            </Button>
                        </Box>
                    }
                />
            )}

            <BackdropComponent loading={isFinalizing || isCancelling} />
        </Box>
    );
}

export default SiniestroDetail;
